/**
 * Объём: движение, подтверждённое участием.
 *
 * Цена может сдвинуться и на пустом рынке; объём показывает, пришли ли
 * за движением деньги. OBV — накопительный объём: растёт в дни роста,
 * падает в дни падения, и нередко поворачивает раньше цены.
 */
import { above, adx, against, atr, breakOut, close, crossUp, ema, extremeBefore, ind, mirror, number, previous, rvol, scaled, spec, weight, below, } from './kit.js';
const obv = ind('obv', {});
export const VOLUME_TEMPLATES = [
    {
        id: 'volume-spike',
        name: 'Всплеск объёма',
        icon: '📊',
        category: 'volume',
        popular: true,
        hint: 'Объём вдвое выше обычного: в рынок кто-то пришёл — по ходу цены.',
        steps: ['Цена по нужную сторону от EMA 20', 'Объём вдвое выше обычного', 'Сила тренда'],
        keywords: ['объём', 'volume', 'всплеск'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Внезапный всплеск объёма показывает, что пришли крупные деньги. Сторону подсказывает положение цены.',
            why: 'Большие участники не могут войти незаметно — их след виден в объёме.',
            fails: 'Всплеск бывает и на закрытии позиций в конце движения — тогда это кульминация, а не начало.',
            tip: 'Если всплеск случился после долгого движения, проверьте «Кульминацию объёма» — это может быть разворот.',
        },
        build: (o) => spec(o, 'Всплеск объёма', mirror((side) => ({
            context: [side.beyond(close, ema(20))],
            trigger: crossUp(rvol, number(2)),
            confirmations: [weight('c1', above(adx, number(18)), 100)],
        })), 0),
    },
    {
        id: 'volume-breakout',
        name: 'Пробой на объёме',
        icon: '📊',
        category: 'volume',
        hint: 'Пробой экстремума, случившийся на повышенном участии, — не на пустом рынке.',
        steps: ['Объём уже повышен', 'Пробит экстремум 20 баров', 'Тренд силён'],
        keywords: ['объём', 'пробой', 'volume breakout'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Пробой засчитывается, только если в этот момент объём в полтора раза выше обычного.',
            why: 'Пробой на пустом рынке легко вернуть обратно; на объёме — за ним стоят реальные заявки.',
            fails: 'На новостях объём высокий у всех движений, и фильтр перестаёт отличать.',
            tip: 'Одна из самых простых и понятных идей для первого бота.',
        },
        build: (o) => spec(o, 'Пробой на объёме', mirror((side) => ({
            context: [above(rvol, number(1.5))],
            trigger: breakOut(side, 20),
            confirmations: [weight('c1', above(adx, number(20)), 100)],
        })), 0),
    },
    {
        id: 'volume-confirmation',
        name: 'Объём подтверждает цену',
        icon: '📊',
        category: 'volume',
        hint: 'Простое пересечение средней — но только когда за ним стоит объём.',
        steps: ['Цена пересекла EMA 20', 'Объём выше обычного в полтора раза'],
        keywords: ['объём', 'подтверждение'],
        passport: {
            level: 'novice',
            market: 'any',
            horizon: 'swing',
            style: 'with-trend',
            timeframe: '4H',
            idea: 'Цена пересекла среднюю, и в ту же свечу объём в полтора раза выше обычного.',
            why: 'Пересечения средней бывают постоянно; объём отделяет решительные от случайных.',
            fails: 'В боковике даже пересечения на объёме часто возвращаются.',
            tip: 'Порог силы 100 требует объёма: снизьте его, и увидите, сколько пересечений было без него.',
        },
        build: (o) => spec(o, 'Объём подтверждает цену', mirror((side) => ({
            context: [],
            trigger: side.cross(close, ema(20)),
            confirmations: [weight('c1', above(rvol, number(1.5)), 100)],
        })), 100),
    },
    /* ---------------------------------------------------------- новые */
    {
        id: 'obv-lead',
        name: 'OBV пробивает раньше цены',
        icon: '📈',
        category: 'volume',
        hint: 'Накопительный объём уже обновил свой экстремум, а цена ещё нет — деньги пришли первыми.',
        steps: ['Цена ещё внутри диапазона 20 баров', 'OBV пробил свой экстремум 20 баров', 'Объём и тренд'],
        keywords: ['obv', 'on balance volume', 'накопительный объём', 'опережение'],
        passport: {
            level: 'skilled',
            market: 'any',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'OBV складывает объём дней роста и вычитает объём дней падения. Если он пробил свой максимум раньше цены — покупают заранее.',
            why: 'Крупные участники набирают позицию, пока цена стоит; OBV показывает этот набор до пробоя.',
            fails: 'OBV может «пробить» и на одном крупном дне без продолжения.',
            tip: 'Хорошее раннее предупреждение: следите за пробоем цены после сигнала.',
        },
        build: (o) => spec(o, 'OBV пробивает раньше цены', mirror((side) => ({
            context: [against(side)(close, extremeBefore(side.edge, 20, side.sign < 0))],
            trigger: side.cross(obv, extremeBefore(obv, 20, side.sign < 0)),
            confirmations: [weight('c1', above(rvol, number(1.2)), 50), weight('c2', side.beyond(close, ema(50)), 50)],
        })), 0),
    },
    {
        id: 'quiet-accumulation',
        name: 'Тихое накопление',
        icon: '🤫',
        category: 'volume',
        hint: 'Цена почти не двигалась, а накопительный объём рос — затем цена вышла из диапазона.',
        steps: ['Размах был ниже обычного', 'OBV за 20 баров вырос в нашу сторону', 'Пробит экстремум 10 баров'],
        keywords: ['накопление', 'accumulation', 'obv', 'вайкофф', 'wyckoff'],
        passport: {
            level: 'pro',
            market: 'range',
            horizon: 'swing',
            style: 'breakout',
            timeframe: '4H',
            idea: 'Идея Вайкоффа: перед ростом крупные тихо покупают в боковике. Цена стоит, а объём в дни роста больше, чем в дни падения.',
            why: 'Когда предложение выкуплено, цене достаточно небольшого толчка, чтобы выйти из диапазона.',
            fails: 'Накопление может оказаться распределением: объём рос, а выход случился вниз.',
            tip: 'Смотрите, в какую сторону растёт OBV, — это и есть ставка на направление выхода.',
        },
        build: (o) => spec(o, 'Тихое накопление', mirror((side) => ({
            context: [below(previous(atr(14), 3), scaled(atr(50), 0.9)), side.beyond(obv, previous(obv, 20))],
            trigger: breakOut(side, 10),
            confirmations: [weight('c1', above(rvol, number(1.3)), 60), weight('c2', above(adx, number(15)), 40)],
        })), 0),
    },
];
//# sourceMappingURL=volume.js.map